import { isCode128Encodable } from './code128.js';
import { CONDITION_FONT_SIZE, CONDITION_TEXT, FNSKU_FONT_SIZE, TITLE_FONT_SIZE } from './layout.js';
import type { LabelJobItem } from './pdf.js';
import { truncateMiddle } from './title.js';

/**
 * The FNSKU label in DYMO Connect's own label format, and a whole print job as
 * the XML its web service takes. Pure: strings in, strings out, unit-tested
 * against known output.
 *
 * The label is the same four things the PDF prints (pdf.ts) — the FNSKU as a
 * Code 128 barcode, the FNSKU in text, the title cut in the middle, and the
 * condition — but here the barcode is a **printer-native barcode object**:
 * DYMO Connect draws the bars itself at the print head's 300 dpi, rather than
 * the app drawing rectangles that a driver then rescales. So this file has no
 * bar widths in it; it names the symbology (`Code128Auto`) and the data.
 *
 * The format (`<DesktopLabel><DYMOLabel>...`) is DYMO Connect's `.dymo` file
 * format. Every element name and every enumerated value used below is taken
 * from label files DYMO Connect itself wrote — DYMO's own SDK samples
 * (github.com/dymosoftware/DCD-SDK-Sample: a text label and a `Code128Auto`
 * barcode label) and two 30336 labels saved from the application — and nothing
 * is used that those files do not show, because the service refuses a label it
 * cannot read as a whole. Lengths are inches from the label's top-left corner.
 *
 * NOT VERIFIED ON A PRINTER: there was no DYMO Connect and no LabelWriter where
 * this was written. docs/labels.md has the checklist for the first real print.
 */

/**
 * The stock: DYMO 30336, 1 x 2-1/8 in, printed landscape. `Small30336` is the
 * name DYMO Connect gives it, and the rectangle is the printable area it
 * records for it (both read from 30336 labels saved by DYMO Connect).
 */
export const DYMO_30336 = {
  labelName: 'Small30336',
  /** The roll's DYMO part number, as printed on the box. */
  sku: '30336',
  printable: { x: 0.09, y: 0.0566667, width: 1.9766667, height: 0.9033333 },
} as const;

/** The names of the label's objects; the label-set records address them by name. */
export const OBJECT_BARCODE = 'BARCODE';
export const OBJECT_FNSKU = 'FNSKU';
export const OBJECT_TITLE = 'TITLE';
export const OBJECT_CONDITION = 'CONDITION';

/**
 * The barcode object's size, one of DYMO Connect's `Small` / `Medium` / `Large`.
 * `Small` is the one that leaves a ten-character FNSKU clear space on both sides
 * of a 2-1/8 in label. If the first printed labels scan poorly, this is the one
 * value to change (and the roll PDF, with its exact 10 mil bars, is the
 * alternative in the meantime).
 */
export const BARCODE_SIZE: 'Small' | 'Medium' | 'Large' = 'Small';

/** The font DYMO Connect draws the text in. Arial is on every Windows computer. */
export const DYMO_FONT = 'Arial';

/** A box on the label: inches from the top-left corner. */
interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

const AREA = DYMO_30336.printable;

/** The four rows of the label, top to bottom, across the whole printable width. */
export const DYMO_LAYOUT: Record<'barcode' | 'fnsku' | 'title' | 'condition', Box> = {
  barcode: { x: AREA.x, y: 0.07, width: AREA.width, height: 0.42 },
  fnsku: { x: AREA.x, y: 0.5, width: AREA.width, height: 0.14 },
  title: { x: AREA.x, y: 0.645, width: AREA.width, height: 0.13 },
  condition: { x: AREA.x, y: 0.78, width: AREA.width, height: 0.13 },
};

/**
 * The widest the title may be, in points, when it is cut: the title row less 4%,
 * because the cut is measured with this browser's Arial and drawn with DYMO
 * Connect's, and the two need not agree to the last hundredth of an inch. (The
 * text objects are also set to shrink to their box, so a title that still came
 * out too wide would be drawn smaller, never clipped.)
 */
export const TITLE_MAX_WIDTH_PT = DYMO_LAYOUT.title.width * 72 * 0.96;

/** Measures a string's width in points at the title's font size. */
export type MeasureText = (text: string) => number;

/**
 * A width estimate for when the browser cannot measure (no canvas): 0.56 of the
 * font size per character, a little wider than Arial's average, so the estimate
 * cuts early rather than late.
 */
export const estimateTitleWidth: MeasureText = (text) =>
  Array.from(text).length * TITLE_FONT_SIZE * 0.56;

/**
 * Text as it can stand inside the label XML: characters XML 1.0 cannot carry
 * (control characters, lone surrogates) are dropped and runs of whitespace
 * become one space. Unlike the PDF's built-in font, DYMO Connect draws with a
 * system font, so accented and non-Latin letters are kept.
 */
export function labelText(text: string): string {
  return Array.from(text)
    .filter((char) => {
      const code = char.codePointAt(0) ?? 0;
      if (code === 0x9 || code === 0xa || code === 0xd) return true;
      if (code < 0x20) return false;
      if (code >= 0x7f && code <= 0x9f) return false;
      if (code >= 0xd800 && code <= 0xdfff) return false;
      return code !== 0xfffe && code !== 0xffff;
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

/** The title line of a label: cleaned, then cut in the middle to fit (title.ts). */
export function dymoTitle(title: string | null, measure: MeasureText): string {
  return truncateMiddle(labelText(title ?? ''), TITLE_MAX_WIDTH_PT, measure);
}

export function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** A length as the label files write it: inches, at most seven decimals, no trailing zeros. */
function inches(value: number): string {
  return String(Number(value.toFixed(7)));
}

const BLACK = '<SolidColorBrush><Color A="1" R="0" G="0" B="0"></Color></SolidColorBrush>';
const CLEAR = '<SolidColorBrush><Color A="0" R="0" G="0" B="0"></Color></SolidColorBrush>';

/** The part every object starts with: its name, brushes, and frame. `fill` is the bars' color. */
function objectHead(name: string, fill: string): string {
  return (
    `<Name>${name}</Name>` +
    '<Brushes>' +
    `<BackgroundBrush>${CLEAR}</BackgroundBrush>` +
    `<BorderBrush>${BLACK}</BorderBrush>` +
    `<StrokeBrush>${BLACK}</StrokeBrush>` +
    `<FillBrush>${fill}</FillBrush>` +
    '</Brushes>' +
    '<Rotation>Rotation0</Rotation>' +
    '<OutlineThickness>1</OutlineThickness>' +
    '<IsOutlined>False</IsOutlined>' +
    '<BorderStyle>SolidLine</BorderStyle>' +
    '<Margin><DYMOThickness Left="0" Top="0" Right="0" Bottom="0" /></Margin>'
  );
}

function fontInfo(size: number): string {
  return (
    '<FontInfo>' +
    `<FontName>${DYMO_FONT}</FontName>` +
    `<FontSize>${size}</FontSize>` +
    '<IsBold>False</IsBold>' +
    '<IsItalic>False</IsItalic>' +
    '<IsUnderline>False</IsUnderline>' +
    `<FontBrush>${BLACK}</FontBrush>` +
    '</FontInfo>'
  );
}

function objectLayout(box: Box): string {
  return (
    '<ObjectLayout>' +
    `<DYMOPoint><X>${inches(box.x)}</X><Y>${inches(box.y)}</Y></DYMOPoint>` +
    `<Size><Width>${inches(box.width)}</Width><Height>${inches(box.height)}</Height></Size>` +
    '</ObjectLayout>'
  );
}

/** One centered line of text that shrinks to its box. */
function textObject(name: string, text: string, size: number, box: Box): string {
  const alignment =
    '<HorizontalAlignment>Center</HorizontalAlignment>' +
    '<VerticalAlignment>Middle</VerticalAlignment>' +
    '<FitMode>AlwaysFit</FitMode>' +
    '<IsVertical>False</IsVertical>';
  return (
    '<TextObject>' +
    objectHead(name, CLEAR) +
    alignment +
    '<FormattedText>' +
    '<FitMode>AlwaysFit</FitMode>' +
    '<HorizontalAlignment>Center</HorizontalAlignment>' +
    '<VerticalAlignment>Middle</VerticalAlignment>' +
    '<IsVertical>False</IsVertical>' +
    '<LineTextSpan><TextSpan>' +
    `<Text>${escapeXml(text)}</Text>` +
    fontInfo(size) +
    '</TextSpan></LineTextSpan>' +
    '</FormattedText>' +
    objectLayout(box) +
    '</TextObject>'
  );
}

/** The FNSKU as a Code 128 barcode DYMO Connect draws itself, with no text of its own. */
function barcodeObject(fnsku: string, box: Box): string {
  return (
    '<BarcodeObject>' +
    objectHead(OBJECT_BARCODE, BLACK) +
    '<BarcodeFormat>Code128Auto</BarcodeFormat>' +
    `<Data><MultiDataString><DataString>${escapeXml(fnsku)}</DataString></MultiDataString></Data>` +
    '<HorizontalAlignment>Center</HorizontalAlignment>' +
    '<VerticalAlignment>Middle</VerticalAlignment>' +
    `<Size>${BARCODE_SIZE}</Size>` +
    '<TextPosition>None</TextPosition>' +
    fontInfo(FNSKU_FONT_SIZE) +
    objectLayout(box) +
    '</BarcodeObject>'
  );
}

/** What one label carries, already cleaned and cut. */
export interface DymoLabelContent {
  fnsku: string;
  title: string;
}

/**
 * One 30336 label as a DYMO Connect label document. A product with no title
 * keeps its (empty) title object, so every label of a job has the same four
 * objects and a label-set record can always address them.
 */
export function buildDymoLabelXml(content: DymoLabelContent): string {
  const { labelName, printable } = DYMO_30336;
  const objects =
    barcodeObject(content.fnsku, DYMO_LAYOUT.barcode) +
    textObject(OBJECT_FNSKU, content.fnsku, FNSKU_FONT_SIZE, DYMO_LAYOUT.fnsku) +
    textObject(OBJECT_TITLE, content.title, TITLE_FONT_SIZE, DYMO_LAYOUT.title) +
    textObject(OBJECT_CONDITION, CONDITION_TEXT, CONDITION_FONT_SIZE, DYMO_LAYOUT.condition);
  return (
    '<?xml version="1.0" encoding="utf-8"?>' +
    '<DesktopLabel Version="1">' +
    '<DYMOLabel Version="3">' +
    '<Description>DYMO Label</Description>' +
    '<Orientation>Landscape</Orientation>' +
    `<LabelName>${labelName}</LabelName>` +
    '<InitialLength>0</InitialLength>' +
    '<BorderStyle>SolidLine</BorderStyle>' +
    '<DYMORect>' +
    `<DYMOPoint><X>${inches(printable.x)}</X><Y>${inches(printable.y)}</Y></DYMOPoint>` +
    `<Size><Width>${inches(printable.width)}</Width><Height>${inches(printable.height)}</Height></Size>` +
    '</DYMORect>' +
    `<BorderColor>${BLACK}</BorderColor>` +
    '<BorderThickness>1</BorderThickness>' +
    '<Show_Border>False</Show_Border>' +
    '<DynamicLayoutManager>' +
    '<RotationBehavior>ClearObjects</RotationBehavior>' +
    `<LabelObjects>${objects}</LabelObjects>` +
    '</DynamicLayoutManager>' +
    '</DYMOLabel>' +
    '<LabelApplication>Blank</LabelApplication>' +
    '<DataTable><Columns></Columns><Rows></Rows></DataTable>' +
    '</DesktopLabel>'
  );
}

/**
 * A label set: one `<LabelRecord>` per label to print, each naming the objects
 * it fills (the format DYMO's `LabelSetBuilder.toXml` writes). The service
 * prints the label once per record, in order, as ONE print job.
 */
export function buildLabelSetXml(records: readonly DymoLabelContent[]): string {
  const body = records
    .map(
      (record) =>
        '<LabelRecord>' +
        `<ObjectData Name="${OBJECT_BARCODE}">${escapeXml(record.fnsku)}</ObjectData>` +
        `<ObjectData Name="${OBJECT_FNSKU}">${escapeXml(record.fnsku)}</ObjectData>` +
        `<ObjectData Name="${OBJECT_TITLE}">${escapeXml(record.title)}</ObjectData>` +
        '</LabelRecord>',
    )
    .join('');
  return `<LabelSet>${body}</LabelSet>`;
}

/**
 * How a list becomes print calls:
 *   - `label-set`: ONE call for the whole list — the label once, plus a label
 *     set with a record per label (a product with a quantity of 5 is five
 *     records). One print job, list order kept. This is the intended way.
 *   - `per-label`: one call per label, each a complete label with its own data
 *     and no label set. Many small jobs, but it relies on nothing beyond
 *     "print this label".
 * This one line is the whole switch. It exists because the label-set route
 * could not be tried on a printer when this was written: if the first real
 * print of a three-product list comes out as one label, or as copies of the
 * first product, set this to `per-label` (docs/labels.md, "First real print").
 */
export const DYMO_JOB_MODE: 'label-set' | 'per-label' = 'label-set';

/** One call to the service's PrintLabel. */
export interface DymoPrintCall {
  labelXml: string;
  labelSetXml: string;
  /** How many labels this call prints. */
  labels: number;
}

export interface DymoJob {
  calls: DymoPrintCall[];
  labels: number;
}

/** Why an FNSKU cannot be printed as a barcode; null when it can. */
export function dymoLabelProblem(fnsku: string): string | null {
  if (!isCode128Encodable(fnsku)) return 'The FNSKU has a character a barcode cannot carry.';
  // A ten-character FNSKU is what the label is laid out for; a much longer one
  // would be drawn too wide for the stock, and the service would not say so.
  if (fnsku.length > 14) return 'The FNSKU is too long for a barcode on this label.';
  return null;
}

/**
 * Turns the list's printable items into the calls that print them, in list
 * order, each product repeated by its quantity. Throws when there is nothing to
 * print or an FNSKU cannot be a barcode.
 */
export function buildDymoJob(
  items: readonly LabelJobItem[],
  measure: MeasureText,
  mode: 'label-set' | 'per-label' = DYMO_JOB_MODE,
): DymoJob {
  const records: DymoLabelContent[] = [];
  for (const item of items) {
    const problem = dymoLabelProblem(item.fnsku);
    if (problem) throw new Error(`${item.fnsku}: ${problem}`);
    const content = { fnsku: item.fnsku, title: dymoTitle(item.title, measure) };
    for (let copy = 0; copy < item.qty; copy += 1) records.push(content);
  }
  const [first] = records;
  if (!first) throw new Error('There is nothing to print.');
  if (mode === 'per-label') {
    return {
      calls: records.map((record) => ({
        labelXml: buildDymoLabelXml(record),
        labelSetXml: '',
        labels: 1,
      })),
      labels: records.length,
    };
  }
  return {
    // The label itself carries the first record's data, so even a service that
    // ignored the label set would print a correct label rather than a blank.
    calls: [
      {
        labelXml: buildDymoLabelXml(first),
        labelSetXml: buildLabelSetXml(records),
        labels: records.length,
      },
    ],
    labels: records.length,
  };
}
